/**
 * The policy-review step (PIVOT-299) — `GET /onboarding/policy/review`,
 * `POST /onboarding/policy/approve`. Shown once, after the candidate reviews
 * their profile, before matcher-v2 starts scoring against it: the exact
 * search this account will run, approved in the candidate's own words.
 *
 * CARRY A FACT ACROSS A BOUNDARY; NEVER RE-DERIVE IT FROM A PROXY
 * -----------------------------------------------------------------
 * `content`, `policy_fingerprint`, and `active_policy_version_id` are read
 * ONCE from `getPolicyReview` and posted back to `approvePolicy` completely
 * unchanged. This component never rebuilds the fingerprint, never edits
 * `content` in place, and never fetches a second, fresher copy to approve
 * instead of what is on screen — approving is *this exact review*, not
 * "whatever the draft currently says".
 *
 * The career baseline (PIVOT-367) is shown here read-only, with its review
 * state in words, for the same reason the interview-sourced work rules are:
 * approval must never commit a fact the person never saw. It is corrected on
 * the profile review one screen earlier, not here -- a preference screen that
 * could edit a career fact is exactly the confusion the contract separates.
 *
 * THE EMPLOYER REFUSALS ARE APPROVED ONE AT A TIME (PIVOT-370)
 * -------------------------------------------------------------
 * Each refusal is shown as the exact sentence it will be enforced as, and
 * each carries its own Approve button. Until every one is approved the policy
 * itself stays blocked, so nothing binds on wording the person never read
 * (constitution §V). Hard and soft render as DIFFERENT sentences on purpose:
 * "never show me these" and "show them lower down" are different promises,
 * and one line for both would hide which one was made.
 *
 * Approving re-stamps the rule server-side, so its `rule_id` changes and this
 * screen reloads the whole review rather than patching a row it can no longer
 * address.
 *
 * `409 policy_changed` (the active policy moved since this screen loaded —
 * a second tab approved first, or the candidate reopened the tab after a
 * long pause) has no retry that could succeed: the candidate has nothing to
 * retype, so the only honest recovery is `reload()`, which re-fetches the
 * current policy and starts this screen over on it.
 */

import { useState } from 'react'
import { ApiError } from '../api/client'
import { approveEmployerExclusion, approvePolicy, getPolicyReview } from '../api/onboarding'
import { useApiResource } from '../hooks/useApiResource'
import { useI18n } from '../i18n'
import type { TranslationKey } from '../i18n/types'
import {
  CONTRACT_TYPES,
  LEVELS,
  PAY_GROSS_OR_NET,
  PAY_PERIODS,
  SCHEDULE_KINDS,
  vocabLabel,
} from '../lib/constraints'
import type {
  CareerBaseline,
  CareerBaselineCorrection,
  ContractRule,
  DesiredWorkRule,
  EmployerExclusionRule,
  EmployerExclusionScope,
  LocationRule,
  Locale,
  OrganizationContext,
  PayRule,
  PolicyReviewResponse,
  RuleStrength,
  ScheduleRule,
  UnwantedWorkRule,
  WorkExclusionRule,
} from '../types/domain'
import { UNKNOWN_BASELINE_VALUE } from '../types/domain'
import './PolicyReview.css'

type Translate = (key: TranslationKey, vars?: Record<string, string | number>) => string

interface PolicyReviewProps {
  /** Called once approval actually commits. */
  onApproved: () => void
  /**
   * The baseline confirmation the person made on the profile-review screen
   * (PIVOT-367, A4). Approval commits it in the same transaction as the
   * policy, so onboarding hands it here instead of saving it one screen
   * earlier, where a later failed approval would strand it. `undefined` --
   * the profile page's own re-approval, or a person who touched no baseline
   * control -- commits no career fact.
   */
  careerBaseline?: CareerBaselineCorrection
}

/** The one place a backend blocker code becomes a plain-word field name --
 * reuses the match-setup form's own labels rather than a second set of
 * strings for the same fields, so the two screens never describe "pay
 * currency" two different ways. */
function blockerLabel(code: string, t: Translate): string {
  switch (code) {
    case 'pay_currency_unknown':
      return t('onboarding.matchSetupPayCurrencyLabel')
    case 'pay_period_unknown':
      return t('onboarding.matchSetupPayPeriodLabel')
    case 'pay_gross_or_net_unknown':
      return t('onboarding.matchSetupPayGrossOrNetLabel')
    case 'location_unrecognized':
      return t('onboarding.matchSetupLocationsLabel')
    default:
      return t('onboarding.policyReviewBlockerGeneric')
  }
}

function locationsText(rule: LocationRule | undefined): string | null {
  return rule?.value ? rule.value.locations.join(', ') : null
}

function contractsText(rule: ContractRule | undefined, t: Translate): string | null {
  if (!rule?.value) return null
  return rule.value.contract_types.map((value) => vocabLabel(CONTRACT_TYPES, value, t)).join(', ')
}

/** Allowed and excluded schedule kinds, each translated through
 * `SCHEDULE_KINDS`. Returned separately (rendered as two lines by the caller)
 * rather than joined into one sentence here, because the word that separates
 * them ("Not for me: ...") is itself translated copy, not a neutral
 * separator this function should own. */
function scheduleLines(
  rule: ScheduleRule | undefined,
  t: Translate,
): { allowed: string | null; excluded: string | null } {
  if (!rule?.value) return { allowed: null, excluded: null }
  const label = (values: string[]) =>
    values.length > 0
      ? values.map((value) => vocabLabel(SCHEDULE_KINDS, value, t)).join(', ')
      : null
  return { allowed: label(rule.value.allowed), excluded: label(rule.value.excluded) }
}

function payText(rule: PayRule | undefined, t: Translate): string | null {
  if (!rule?.value) return null
  const { amount, currency, period, gross_or_net: grossOrNet } = rule.value
  const periodLabel = vocabLabel(PAY_PERIODS, period, t)
  const grossOrNetLabel = vocabLabel(PAY_GROSS_OR_NET, grossOrNet, t)
  return `${amount} ${currency} / ${periodLabel} (${grossOrNetLabel})`
}

function exclusionsText(rules: WorkExclusionRule[]): string[] {
  return rules.map((rule) => rule.value?.text).filter((text): text is string => Boolean(text))
}

const LANGUAGES: { value: Locale; labelKey: TranslationKey }[] = [
  { value: 'fr', labelKey: 'onboarding.constraintsLanguageFrench' },
  { value: 'nl', labelKey: 'onboarding.constraintsLanguageDutch' },
  { value: 'en', labelKey: 'onboarding.constraintsLanguageEnglish' },
]

function languageLines(review: PolicyReviewResponse, t: Translate): string[] {
  return review.language_answers.map((answer) => {
    const language = vocabLabel(LANGUAGES, answer.language, t)
    const level = vocabLabel(LEVELS, answer.level, t)
    return `${language}: ${level}`
  })
}

/** One line per desired/unwanted-work rule -- interview-sourced, not asked by
 * this form, but approval commits them right alongside the five categories
 * above (`SearchPolicyVersion.desired_work`/`unwanted_work`), so a rule
 * hidden here would still be an approved effect the candidate never saw
 * (PIVOT-299 review). A blocked rule renders its blocked reason in place of
 * its text, the same way a blocked single-rule section does below -- these
 * two categories can hold more than one rule, so that swap happens per line
 * instead of per section. */
function workRuleLines(
  rules: (DesiredWorkRule | UnwantedWorkRule)[],
  blockers: Record<string, string[]>,
  t: Translate,
): string[] {
  return rules
    .map((rule) => {
      const ruleBlockers = blockers[rule.rule_id]
      if (ruleBlockers && ruleBlockers.length > 0) {
        return t('onboarding.policyReviewBlocked', {
          reason: ruleBlockers.map((code) => blockerLabel(code, t)).join(', '),
        })
      }
      return rule.value
    })
    .filter((line): line is string => Boolean(line))
}

/** The one place a (scope, strength) pair becomes the sentence the person
 * approves. Four distinct lines, never one line with a modifier: the wording
 * IS the promise, and the server hashes the same pair into the meaning token
 * it verifies the approval against. A scope this screen has no sentence for
 * renders its raw name rather than nothing -- an unshown rule must never be
 * approvable. */
function employerMeaningKey(
  scope: EmployerExclusionScope,
  strength: RuleStrength,
): TranslationKey | null {
  if (scope === 'all_commercial') {
    return strength === 'hard'
      ? 'onboarding.policyReviewEmployerAllCommercialHard'
      : 'onboarding.policyReviewEmployerAllCommercialSoft'
  }
  if (scope === 'large_corporate') {
    return strength === 'hard'
      ? 'onboarding.policyReviewEmployerLargeCorporateHard'
      : 'onboarding.policyReviewEmployerLargeCorporateSoft'
  }
  return null
}

/** The organization kinds a listed refusal covers, in the person's own words.
 * The wire value is the fallback for a kind this table does not know yet --
 * a rule whose contents were hidden could be approved unseen, which is the
 * one thing this screen exists to prevent (constitution §V). */
const ORGANIZATION_CONTEXTS: { value: OrganizationContext; labelKey: TranslationKey }[] = [
  { value: 'public_service', labelKey: 'onboarding.policyReviewOrganizationPublicService' },
  { value: 'nonprofit', labelKey: 'onboarding.policyReviewOrganizationNonprofit' },
  { value: 'academic', labelKey: 'onboarding.policyReviewOrganizationAcademic' },
  { value: 'commercial', labelKey: 'onboarding.policyReviewOrganizationCommercial' },
  { value: 'large_commercial', labelKey: 'onboarding.policyReviewOrganizationLargeCommercial' },
]

/** The sentence for the third scope: a refusal that names its own list of
 * organization kinds. It spells out BOTH halves the person is consenting to
 * -- which kinds, and whether the rule removes a job or only ranks it down.
 * Showing the scope name alone would record an approval of specific kinds and
 * a strength the person never read (review finding F3). */
function employerTypesText(
  types: OrganizationContext[],
  strength: RuleStrength,
  t: Translate,
): string {
  const listed = types.map((value) => vocabLabel(ORGANIZATION_CONTEXTS, value, t)).join(', ')
  return t(
    strength === 'hard'
      ? 'onboarding.policyReviewEmployerTypesHard'
      : 'onboarding.policyReviewEmployerTypesSoft',
    { types: listed },
  )
}

function employerMeaningText(rule: EmployerExclusionRule, t: Translate): string {
  if (!rule.value) return t('onboarding.policyReviewNoPreference')
  const key = employerMeaningKey(rule.value.scope, rule.strength)
  if (key) return t(key)
  if (rule.value.scope === 'organization_types') {
    return employerTypesText(rule.value.organization_types, rule.strength, t)
  }
  return t('onboarding.policyReviewEmployerOtherScope', { scope: rule.value.scope })
}

/** The baseline's review state, in the person's own language (A5). */
function baselineStateKey(
  baseline: CareerBaseline | null,
  pending: CareerBaselineCorrection | undefined,
): TranslationKey {
  // A pending confirmation is what this approval will actually write, so it
  // is what the screen shows. Reading the stored row instead would display
  // the value the person just replaced (A5: approve nothing unseen).
  if (pending) return 'onboarding.baselineStateConfirmed'
  if (baseline === null) return 'onboarding.baselineStateMissing'
  switch (baseline.review_state) {
    case 'baseline_confirmed':
      return 'onboarding.baselineStateConfirmed'
    case 'baseline_unknown':
      return 'onboarding.baselineStateUnknown'
    default:
      return 'onboarding.baselineStateUnconfirmed'
  }
}

/** The job on file, or the words for "not known yet". Never a blank line. */
function baselineRoleText(
  baseline: CareerBaseline | null,
  pending: CareerBaselineCorrection | undefined,
  t: Translate,
): string {
  const role = pending ? pending.role_title : (baseline?.role_title ?? UNKNOWN_BASELINE_VALUE)
  return role === UNKNOWN_BASELINE_VALUE ? t('onboarding.baselineUnknownValue') : role
}

interface Section {
  key: string
  labelKey: TranslationKey
  ruleId: string | undefined
  value: string | null
  list?: string[]
}

function sectionsFor(
  review: PolicyReviewResponse,
  t: Translate,
  pendingBaseline?: CareerBaselineCorrection,
): Section[] {
  const [location] = review.content.location_rules
  const [contract] = review.content.contract_rules
  const [schedule] = review.content.schedule_rules
  const [pay] = review.content.pay_rules
  const scheduleValue = scheduleLines(schedule, t)
  const scheduleList = [
    scheduleValue.allowed &&
      `${t('onboarding.matchSetupScheduleAllowedLabel')}: ${scheduleValue.allowed}`,
    scheduleValue.excluded &&
      `${t('onboarding.matchSetupScheduleExcludedLabel')}: ${scheduleValue.excluded}`,
  ].filter((line): line is string => Boolean(line))
  return [
    {
      key: 'locations',
      labelKey: 'onboarding.policyReviewLocationsLabel',
      ruleId: location?.rule_id,
      value: locationsText(location),
    },
    {
      key: 'contracts',
      labelKey: 'onboarding.policyReviewContractLabel',
      ruleId: contract?.rule_id,
      value: contractsText(contract, t),
    },
    {
      key: 'schedule',
      labelKey: 'onboarding.policyReviewScheduleLabel',
      ruleId: schedule?.rule_id,
      value: null,
      list: schedule?.value ? scheduleList : undefined,
    },
    {
      key: 'pay',
      labelKey: 'onboarding.policyReviewPayLabel',
      ruleId: pay?.rule_id,
      value: payText(pay, t),
    },
    {
      key: 'languages',
      labelKey: 'onboarding.matchSetupLanguagesLabel',
      ruleId: undefined,
      value: null,
      list: languageLines(review, t),
    },
    {
      key: 'exclusions',
      labelKey: 'onboarding.policyReviewExclusionsLabel',
      ruleId: undefined,
      value: null,
      list: exclusionsText(review.content.occupation_exclusions),
    },
    // Interview-sourced, not asked by this form (see this module's own
    // docstring on `workRuleLines`) -- shown so approval never commits a rule
    // the candidate never saw on this screen.
    {
      key: 'desired-work',
      labelKey: 'onboarding.policyReviewDesiredWorkLabel',
      ruleId: undefined,
      value: null,
      list: workRuleLines(review.content.desired_work, review.blockers, t),
    },
    {
      key: 'unwanted-work',
      labelKey: 'onboarding.policyReviewUnwantedWorkLabel',
      ruleId: undefined,
      value: null,
      list: workRuleLines(review.content.unwanted_work, review.blockers, t),
    },
    // The career baseline (PIVOT-367). Read-only here: two lines, the job and
    // the state that says how much authority it carries.
    {
      key: 'career-baseline',
      labelKey: 'onboarding.baselineEyebrow',
      ruleId: undefined,
      value: null,
      list: [
        baselineRoleText(review.career_baseline, pendingBaseline, t),
        t(baselineStateKey(review.career_baseline, pendingBaseline)),
      ],
    },
  ]
}

export function PolicyReview({ onApproved, careerBaseline }: PolicyReviewProps) {
  const { t } = useI18n()
  const review = useApiResource(getPolicyReview)
  const [approving, setApproving] = useState(false)
  const [error, setError] = useState<TranslationKey | null>(null)
  const [changedElsewhere, setChangedElsewhere] = useState(false)
  const [approvingRuleId, setApprovingRuleId] = useState<string | null>(null)
  const [employerError, setEmployerError] = useState(false)

  if (review.loading && !review.data) {
    return <output>{t('common.loading')}</output>
  }
  if (review.failed && !review.data) {
    return (
      <div className="stack">
        <p className="form-error" role="alert">
          {t('onboarding.policyReviewLoadFailed')}
        </p>
        <button type="button" className="btn btn-primary" onClick={() => void review.reload()}>
          {t('onboarding.retry')}
        </button>
      </div>
    )
  }
  if (!review.data) return <output>{t('common.loading')}</output>
  // Captured once the guard above proves it non-null: a closure below
  // (`submit`) reads it too, and TypeScript cannot narrow through a closure
  // boundary on its own.
  const { data } = review

  const hasBlockers = Object.keys(data.blockers).length > 0

  /** Approve ONE refusal's wording, then reload: the server re-stamps the
   * rule, so the id this screen is holding stops existing the moment the
   * call succeeds (PIVOT-370, A4). */
  const approveWording = async (
    ruleId: string,
    displayedMeaning: string | undefined,
    strength: RuleStrength,
  ) => {
    if (displayedMeaning === undefined) return
    setEmployerError(false)
    setApprovingRuleId(ruleId)
    try {
      await approveEmployerExclusion(ruleId, displayedMeaning, strength)
    } catch {
      setEmployerError(true)
      setApprovingRuleId(null)
      return
    }
    await review.reload()
    setApprovingRuleId(null)
  }

  const submit = async () => {
    setError(null)
    setChangedElsewhere(false)
    setApproving(true)
    try {
      // `data` as fetched for THIS render, not a re-fetched copy — see the
      // module docstring. The baseline confirmation rides along so it commits
      // with the policy version, never before it (PIVOT-367, A4).
      await approvePolicy(data, careerBaseline)
    } catch (reason) {
      setApproving(false)
      if (reason instanceof ApiError && reason.status === 409) {
        setChangedElsewhere(true)
        return
      }
      setError('onboarding.policyReviewApproveFailed')
      return
    }
    setApproving(false)
    onApproved()
  }

  return (
    <div className="stack">
      <h2>{t('onboarding.policyReviewTitle')}</h2>
      <p>{t('onboarding.policyReviewBody')}</p>

      {data.content.employer_exclusions.length > 0 && (
        <section className="policy-review-employers">
          <h3 className="policy-review-label">{t('onboarding.policyReviewEmployerLabel')}</h3>
          <ul className="policy-review-list">
            {data.content.employer_exclusions.map((rule) => {
              const meaning = data.employer_exclusion_meanings[rule.rule_id]
              const approved = rule.value?.approved_meaning !== null
              return (
                <li key={rule.rule_id} className="policy-review-employer">
                  <span>{employerMeaningText(rule, t)}</span>
                  {approved ? (
                    <span className="policy-review-employer-state">
                      {t('onboarding.policyReviewEmployerApproved')}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-outline"
                      aria-busy={approvingRuleId === rule.rule_id}
                      disabled={approvingRuleId !== null || meaning === undefined}
                      onClick={() => void approveWording(rule.rule_id, meaning, rule.strength)}
                    >
                      {t('onboarding.policyReviewEmployerApprove')}
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
          {employerError && (
            <p className="form-error" role="alert">
              {t('onboarding.policyReviewEmployerApproveFailed')}
            </p>
          )}
        </section>
      )}

      <dl className="policy-review">
        {sectionsFor(data, t, careerBaseline).map((section) => {
          const blockers = section.ruleId ? data.blockers[section.ruleId] : undefined
          return (
            <div key={section.key} className="policy-review-section">
              <dt className="policy-review-label">{t(section.labelKey)}</dt>
              <dd className="policy-review-value">
                {blockers && blockers.length > 0 ? (
                  <span className="policy-review-blocked">
                    {t('onboarding.policyReviewBlocked', {
                      reason: blockers.map((code) => blockerLabel(code, t)).join(', '),
                    })}
                  </span>
                ) : section.list ? (
                  section.list.length > 0 ? (
                    <ul className="policy-review-list">
                      {section.list.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    t('onboarding.policyReviewNoPreference')
                  )
                ) : (
                  (section.value ?? t('onboarding.policyReviewNoPreference'))
                )}
              </dd>
            </div>
          )
        })}
      </dl>

      {changedElsewhere && (
        <div className="stack">
          <p className="form-error" role="alert">
            {t('onboarding.policyReviewChangedElsewhere')}
          </p>
          <button type="button" className="btn btn-primary" onClick={() => void review.reload()}>
            {t('onboarding.policyReviewReload')}
          </button>
        </div>
      )}

      {error && !changedElsewhere && (
        <p className="form-error" role="alert">
          {t(error)}
        </p>
      )}

      {!changedElsewhere && (
        <button
          type="button"
          className="btn btn-primary"
          aria-busy={approving}
          disabled={approving || hasBlockers || data.policy_fingerprint === null}
          onClick={() => void submit()}
        >
          {t('onboarding.policyReviewApprove')}
        </button>
      )}
    </div>
  )
}
